import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { formatDate } from '../lib/dates'
import { formatCurrency } from '../lib/jobFinancials'

export default function Estimates() {
  const navigate = useNavigate()
  const [estimates, setEstimates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sending, setSending] = useState({})

  useEffect(() => { fetchEstimates() }, [])

  // Closes the loop from the estimate side: if a proposal has already been generated
  // from this estimate (see EstimateBuilder.generateProposal), show a link to it. The
  // embedded `proposals` filter keeps only the current version per estimate — matches
  // Proposals.js default view. One query, one error path, instead of a second round-trip.
  async function fetchEstimates() {
    const { data, error } = await supabase
      .from('estimates')
      .select('*, client:clients(name, email, phone), proposals(id, proposal_number, status, created_at, is_current_version)')
      .eq('proposals.is_current_version', true)
      .order('created_at', { ascending: false })
    if (error) { setError('Failed to load estimates: ' + error.message); setLoading(false); return }
    setEstimates(data || [])
    setLoading(false)
  }

  async function updateStatus(id, status) {
    const { error } = await supabase.from('estimates').update({ status }).eq('id', id)
    if (error) return setError('Failed to update status: ' + error.message)
    setEstimates(prev => prev.map(e => e.id === id ? { ...e, status } : e))
  }

  async function deleteEstimate(id) {
    if (!window.confirm('Delete this estimate? This cannot be undone.')) return
    const { error } = await supabase.from('estimates').delete().eq('id', id)
    if (error) {
      if (error.code === '23503') return setError('This estimate is still linked to something else (a proposal, or a site visit it was converted from) and can’t be deleted. Remove that link first.')
      return setError('Delete failed: ' + error.message)
    }
    setEstimates(prev => prev.filter(e => e.id !== id))
  }

  async function sendEstimate(est) {
    if (!est.client_id) return setError('This estimate has no client linked.')
    const client = est.client
    if (!client) return setError('Could not load client info.')
    if (!client.email) return setError(`${client.name} has no email address on file. Add one in their client profile first.`)
    if (!window.confirm(`Send estimate to ${client.name} at ${client.email}?`)) return

    setSending(prev => ({ ...prev, [est.id]: true }))
    setError(null)

    const { error: emailErr } = await supabase.functions.invoke('send-email', {
      body: {
        template: 'estimate_sent',
        to: client.email,
        data: {
          client_name: client.name,
          job_title: est.description || est.project_name || 'Your Project',
          amount: (est.suggested_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          valid_until: est.expiry_date ? formatDate(est.expiry_date, 'long') : '',
          portal_link: `${window.location.origin}/portal`,
          company_email: 'john@tlcunlimitedtx.com',
          company_phone: client.phone || '',
        }
      }
    })

    if (emailErr) {
      setError('Email failed: ' + emailErr.message)
      setSending(prev => ({ ...prev, [est.id]: false }))
      return
    }

    await supabase.from('estimates').update({ status: 'sent' }).eq('id', est.id)
    setEstimates(prev => prev.map(e => e.id === est.id ? { ...e, status: 'sent' } : e))
    setSending(prev => ({ ...prev, [est.id]: false }))
    alert(`Estimate sent to ${client.name} at ${client.email}`)
  }

  const statusColor = {
    draft: '#6b7280',
    sent: '#2563eb',
    approved: '#16a34a',
    declined: '#dc2626'
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Estimates</h1>
          <p style={s.sub}>{estimates.length} total · Build detailed quotes for clients</p>
        </div>
        <div style={s.headerActions}>
          <button style={s.materialsBtn} onClick={() => navigate('/materials')}>⚙ Material Price List</button>
          <button style={s.addBtn} onClick={() => navigate('/estimates/new')}>+ New Estimate</button>
        </div>
      </div>

      {error && (
        <div style={s.errorBox}>
          <span>{error}</span>
          <button style={s.errorClose} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.loading}>Loading estimates...</div>
      ) : estimates.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
          <div style={{ fontWeight: '600', color: '#374151', marginBottom: '6px' }}>No estimates yet</div>
          <div style={{ color: '#6b7c6b', fontSize: '14px', marginBottom: '20px' }}>Create your first estimate to start quoting jobs</div>
          <button style={s.addBtn} onClick={() => navigate('/estimates/new')}>+ Create Estimate</button>
        </div>
      ) : (
        <div style={s.list}>
          {estimates.map(est => (
            <div
              key={est.id}
              className="row-hover"
              style={{ ...s.card, cursor: 'pointer' }}
              onClick={() => navigate(`/estimates/${est.id}/edit`)}
            >
              <div style={s.cardLeft}>
                <div style={s.estRow}>
                  <span style={s.estTitle}>
                    {est.quote_number ? `Quote #${est.quote_number} — ` : ''}{est.client?.name || 'No client'}
                  </span>
                  <span style={{ ...s.statusBadge, background: statusColor[est.status] || '#6b7280' }}>{est.status}</span>
                </div>
                {est.description && <span style={s.estDesc}>{est.description}</span>}
                <span style={s.estDate}>{formatDate(est.created_at)}</span>
                {est.proposals?.[0] && (
                  <button
                    style={s.proposalLink}
                    onClick={e => { e.stopPropagation(); navigate(`/proposals/${est.proposals[0].id}`) }}
                    title={`View ${est.proposals[0].proposal_number} (${est.proposals[0].status})`}
                  >
                    📄 View Proposal {est.proposals[0].proposal_number}
                  </button>
                )}
              </div>
              <div style={s.cardRight}>
                <span style={s.estPrice}>{formatCurrency(est.suggested_price)}</span>
                <div style={s.estActions} onClick={e => e.stopPropagation()}>
                  <button style={s.editBtn} onClick={() => navigate(`/estimates/${est.id}/edit`)}>Edit</button>

                  {/* Send — active when client has email */}
                  {est.client_id && est.client?.email && (
                    <button
                      style={{ ...s.sendBtn, opacity: sending[est.id] ? 0.6 : 1 }}
                      onClick={() => sendEstimate(est)}
                      disabled={!!sending[est.id]}
                    >
                      {sending[est.id] ? 'Sending...' : '✉ Send'}
                    </button>
                  )}

                  {/* Send — dimmed when client has no email */}
                  {est.client_id && !est.client?.email && (
                    <button
                      style={{ ...s.sendBtn, opacity: 0.4, cursor: 'not-allowed' }}
                      title="Add an email to this client to enable sending"
                      onClick={() => setError(`${est.client?.name || 'This client'} has no email on file. Add one in their client profile.`)}
                    >
                      ✉ Send
                    </button>
                  )}

                  {est.status !== 'approved' && (
                    <button
                      style={s.convertBtn}
                      onClick={() => navigate(`/estimates/${est.id}/edit`)}
                      title="Open this estimate to convert it to a job with full cost baseline"
                    >
                      Open to Convert
                    </button>
                  )}
                  <select
                    style={s.statusSelect}
                    value={est.status}
                    onChange={e => updateStatus(est.id, e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                  <button style={s.deleteBtn} onClick={() => deleteEstimate(est.id)}>✕</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const s = {
  page: { width: '100%' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title: { margin: 0, fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '4px 0 0', color: '#6b7c6b', fontSize: '14px' },
  headerActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  materialsBtn: { background: '#f3f4f6', border: '1.5px solid #d1d5db', color: '#374151', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  addBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', fontSize: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  errorClose: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '0 4px', fontFamily: 'inherit', flexShrink: 0 },
  loading: { color: '#6b7c6b', padding: '40px 0', textAlign: 'center' },
  empty: { textAlign: 'center', padding: '60px 0', color: '#6b7c6b' },
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderRadius: '10px', padding: '16px 20px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)', gap: '12px', flexWrap: 'wrap' },
  cardLeft: { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 },
  estRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  estTitle: { fontWeight: '600', color: '#1a2e1a', fontSize: '15px' },
  estDesc: { fontSize: '13px', color: '#6b7c6b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '500px' },
  estDate: { fontSize: '12px', color: '#9ca3af' },
  proposalLink: { alignSelf: 'flex-start', background: '#ede9fe', border: '1px solid #ddd6fe', color: '#6d28d9', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', fontFamily: 'inherit', marginTop: '2px' },
  cardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' },
  estPrice: { fontWeight: '700', color: '#2d4a2d', fontSize: '20px' },
  statusBadge: { color: 'white', padding: '3px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize' },
  estActions: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' },
  editBtn: { background: 'none', border: '1px solid #d1d5db', color: '#374151', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', fontFamily: 'inherit' },
  sendBtn: { background: '#0369a1', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit', transition: 'opacity 0.15s' },
  convertBtn: { background: '#1e3a5f', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'inherit' },
  statusSelect: { padding: '5px 8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'inherit', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #fecaca', color: '#dc2626', cursor: 'pointer', fontSize: '13px', padding: '4px 8px', borderRadius: '6px', fontFamily: 'inherit' },
}
